/* eslint-disable max-lines-per-function */
import axios, {AxiosError, AxiosResponse, InternalAxiosRequestConfig} from 'axios';
// eslint-disable-next-line import/no-cycle
import router from '@client/router';
import {getTokens, logout, NoCurrentUserError} from '@simpleinvest/front-end-auth';
import windowHelper from '@client/util/windowHelper';
import {parse, ServiceCommonResponseError} from '@client/util/errorMessageParser';
import {Logger, Category} from '@simpleinvest/front-end-logger';
import {MicroserviceError} from '@simpleinvest/ms-core-sdk';
import {ClientWindow} from '@client/custom-window';
import {displayErrorBanner} from '@client/events/notification-bus';

declare let window: ClientWindow;

const logger = new Logger({
  category: Category.Authentication,
  fileName: 'interceptors.ts',
});

const APP_NAME = 'client-platform';
const loginPath = window.BASE_URL;

const processEnvList = new Set([process.env.VUE_APP_COGNITO_DOMAIN, process.env.VUE_APP_COGNITO_CLIENT_ID]);

export const BANNER_ERRORS = [
  {
    errorCode: 400,
    errorMessage:
      'A request was not able to be processed by our services. Information on the page may not be up to date. Error code: 400',
  },
  {
    errorCode: 404,
    errorMessage:
      'A request was not able to be processed by our services. Information on the page may not be up to date. Error code: 404',
  },
  {
    errorCode: 406,
    errorMessage:
      'A request was not accepted by our services. Information on the page may not be up to date. Error code: 406',
  },
  {
    errorCode: 408,
    errorMessage: 'A request was no completed on time. Information on the page may not be up to date. Error code: 408',
  },
  {
    errorCode: 415,
    errorMessage:
      'A request made is not supported by our services. Information on the page may not be up to date. Error code: 415',
  },
  {
    errorCode: 422,
    errorMessage:
      'A request was not able to be processed by our services. Information on the page may not be up to date. Error code: 422',
  },
  {
    errorCode: 429,
    errorMessage:
      'Too many requests were made and could not be processed by our services. Information on the page may not be up to date. Error code: 429',
  },
  {
    errorCode: 500,
    errorMessage:
      'An error occurred with one of our services, and the request did not complete successfully. Information on the page may not be up to date. Error code: 500',
  },
  {
    errorCode: 501,
    errorMessage: 'A request was made that is not supported at this time. Please try again later. Error code: 501',
  },
  {
    errorCode: 502,
    errorMessage: 'A downstream service encountered an error. Please try again later. Error code: 502',
  },
  {
    errorCode: 503,
    errorMessage:
      'One or more services are temporarily unavailable. Information on the page may not be up to date. Error code: 503',
  },
  {
    errorCode: 504,
    errorMessage:
      'A service did not respond within an acceptable time. Information on the page may not be up to date. Error code: 504',
  },
];

export const GENERIC_HTTP_ERROR_MSG =
  'A request was not able to be processed by our services. Information on the page may not be up to date. Error code:';

// eslint-disable-next-line require-await
export const redirectToLogin = async (): Promise<void> => {
  await logout();

  if (window.localStorage.getItem('SSO_PROVIDER')) {
    window.localStorage.removeItem('SSO_PROVIDER');
    const {VUE_APP_COGNITO_DOMAIN, VUE_APP_COGNITO_CLIENT_ID} = process.env;

    if (processEnvList.has(VUE_APP_COGNITO_DOMAIN) && processEnvList.has(VUE_APP_COGNITO_CLIENT_ID)) {
      const url = `https://${[...processEnvList][0]}/logout?client_id=${[...processEnvList][1]}`;

      windowHelper.location.assign(url);
    }
  } else {
    // try to return to the same page on re-login
    const currentUrl = windowHelper.location.href.split(window.BASE_URL).pop();
    const removeRedirectFromUrl = currentUrl.replace(/\?redirect=/, '');
    const removeTokensFromRedirects = removeRedirectFromUrl.split('?#token=').shift();

    windowHelper.location.assign(`${loginPath}?redirect=${removeTokensFromRedirects}`);
  }
};

export const requestInterceptorFulfilledHandler = async (
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> => {
  let cognitoTokens;

  try {
    cognitoTokens = await getTokens();
  } catch (error) {
    if (error instanceof NoCurrentUserError) {
      logger.addBreadcrumb({
        callingFuncName: 'requestInterceptorFulfilledHandler',
        message: error.message,
      });
    } else {
      logger.logException({error});
    }
  }
  const accessToken = cognitoTokens?.accessToken;
  const idToken = cognitoTokens?.idToken;

  // if the header is explicitly set, dont override it
  if (idToken && !config.headers.Authorization) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${idToken}`;
  }

  if (accessToken) {
    // eslint-disable-next-line no-param-reassign
    config.headers['X-Cog-Authorization'] = `Bearer ${accessToken}`;
  }

  // TODO: Remove after PHP is retired. By default we are adding this
  // header to the global axios object for compatibility with Laravel.
  // This isn't on the list of supported CORS headers for the API
  // Gateway.
  // if (config.url?.startsWith('https://api.digitaladvice.individuals.')) {
  // eslint-disable-next-line no-param-reassign
  // delete config.headers.common['X-Requested-With'];
  // }

  // eslint-disable-next-line no-param-reassign
  config.headers.Src_Platform = APP_NAME;

  // eslint-disable-next-line no-param-reassign
  config.headers.Src_Location = window.location.href;

  if (router?.currentRoute?.name) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Src_Component = router.currentRoute.name;
  }

  if (config.url?.includes('client-bff')) {
    // eslint-disable-next-line no-param-reassign
    config.headers['X-API-Key'] = process.env.VUE_APP_BFF_API_GATEWAY_KEY;
  }

  return config;
};

export const requestInterceptorRejectedHandler = (
  error: AxiosError<ServiceCommonResponseError>
): Promise<AxiosError<ServiceCommonResponseError>> => Promise.reject(error);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const responseInterceptorFulfilledHandler = (response: AxiosResponse<any>): AxiosResponse<any> => response;

// eslint-disable-next-line complexity, consistent-return
export const responseInterceptorRejectedHandler = async (error: AxiosError<ServiceCommonResponseError>): Promise<Error> => {
  logger.addBreadcrumb({
    callingFuncName: 'responseInterceptorRejectedHandler',
    message: error.message,
  });

  // First, handle redirect to login scenarios.
  if (error.response?.status === 401 || error.response?.status === 403 || error.response?.status === 419) {
    logger.addBreadcrumb({
      callingFuncName: 'responseInterceptorRejectedHandler',
      message: 'Redirecting to login.',
    });

    await redirectToLogin();
  } else {
    const bannerStatusCodes = BANNER_ERRORS.map((err) => err.errorCode);
    const shouldShowGenericBanner =
      error.response?.status > 400 && error.response?.status < 600 && error.response?.status !== 409;

    const isExcludedFromBanner = error.response?.status === 409;

    if (bannerStatusCodes.includes(error.response?.status) && !isExcludedFromBanner) {
      displayErrorBanner(BANNER_ERRORS[bannerStatusCodes.indexOf(error.response?.status)].errorMessage);
      // if (isInternalServerError) {
      //   window.location.reload();
      // }
    } else if (shouldShowGenericBanner) {
      displayErrorBanner(`${GENERIC_HTTP_ERROR_MSG} ${error.response?.status}`);
    }

    // If the call is an SDK call, let the SDK handle the error.
    if (error.config.url?.startsWith('https://api.digitaladvice.individuals.')) {
      logger.addBreadcrumb({
        callingFuncName: 'responseInterceptorRejectedHandler',
        message: 'SDK call. Letting the SDK error handle.',
      });

      // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
      return Promise.reject(error);
    }

    // If the call is an BFF call
    if (error.config.url?.startsWith('https://client.api.')) {
      logger.addBreadcrumb({
        callingFuncName: 'responseInterceptorRejectedHandler',
        message: 'BFF Call',
      });

      // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
      return Promise.reject(error);
    }

    // TODO: Remove after all PHP calls have been removed.
    // Legacy PHP/Proxy error handling
    const {message, cause, traceId, statusCode, resourceId} = parse(error);

    const serviceError = new MicroserviceError({
      serviceName: 'php-proxy',
      path: error.config.url,
      message,
      cause,
      traceId,
      statusCode,
      resourceId,
    });

    // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject
    return Promise.reject(serviceError);
  }
};

// eslint-disable-next-line import/prefer-default-export
export const initialize = (): void => {
  axios.interceptors.response.use(responseInterceptorFulfilledHandler, responseInterceptorRejectedHandler);

  // Add the access_token (if available) to each request
  axios.interceptors.request.use(requestInterceptorFulfilledHandler, requestInterceptorRejectedHandler);
};
